import React, { useEffect } from 'react';
import './SuccessModal.css';
import { X } from 'lucide-react';

const SuccessModal = ({ isOpen, onClose, title, message, details, onContinue }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
      // Close on escape key
      const handleEscape = (e) => {
        if (e.key === 'Escape') onClose();
      };
      document.addEventListener('keydown', handleEscape);
      
      return () => {
        document.body.classList.remove('modal-open');
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, onClose]);

  // Close on overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const isWithdrawal = title?.includes('Withdrawal') || title?.includes('withdrawal');
  const mainColor = isWithdrawal ? '#2563eb' : '#15A759';
  const gradient = isWithdrawal 
    ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
    : 'linear-gradient(135deg, #15A759 0%, #0d8b48 100%)';
  const secondaryColor = isWithdrawal ? 'rgba(37, 99, 235, 0.1)' : 'rgba(21, 167, 89, 0.1)';
  const noticeColor = isWithdrawal ? 'rgba(37, 99, 235, 0.1)' : 'rgba(255, 193, 7, 0.1)';
  const noticeTextColor = isWithdrawal ? '#1e40af' : '#856404';

  if (!isOpen) return null;

  return (
    <div className="success-modal-overlay" onClick={handleOverlayClick}>
      <div className="success-modal-container">
        <div className="success-modal-content">
          {/* Close button */}
          <button className="success-modal-close" onClick={onClose}>
            <X size={24} />
          </button>

          {/* Animated success icon */}
          <div className="success-animation">
            <div className="success-icon">
              <svg className="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                <circle 
                  className="checkmark__circle" 
                  cx="26" 
                  cy="26" 
                  r="25" 
                  fill="none"
                  style={{ stroke: mainColor }}
                />
                <path 
                  className="checkmark__check" 
                  fill="none" 
                  d="M14.1 27.2l7.1 7.2 16.7-16.8"
                  style={{ stroke: mainColor }}
                />
              </svg>
            </div>
            <div className="success-pulse" style={{ background: secondaryColor }}></div>
          </div>

          {/* Title */}
          <h1 
            className="success-title"
            style={{ color: mainColor }}
          >
            {title || "Transaction Successful!"}
          </h1>

          {/* Main message */}
          <p className="success-message">
            {message || "Your request has been received and is being processed."}
          </p>

          {/* Details section */}
          {details && (
            <div className="success-details">
              <h3>Transaction Details</h3>
              <div className="details-grid">
                {Object.entries(details).map(([key, value]) => (
                  <div key={key} className="detail-item">
                    <span className="detail-label">{key}:</span>
                    <span 
                      className="detail-value"
                      style={{ color: mainColor }}
                    >
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Important notice */}
          <div 
            className="success-notice"
            style={{
              background: `linear-gradient(135deg, ${noticeColor} 0%, ${noticeColor.replace('0.1', '0.05')} 100%)`,
              border: `1px solid ${noticeColor.replace('0.1', '0.2')}`
            }}
          >
            <div 
              className="notice-icon"
              style={{ background: noticeColor.replace('0.1', '0.2') }}
            >
              ⏱️
            </div>
            <div className="notice-content">
              <h4 style={{ color: noticeTextColor }}>
                Processing Time
              </h4>
              <p style={{ color: noticeTextColor }}>
                {isWithdrawal 
                  ? "Your withdrawal will be reviewed and processed within 24-48 hours. You'll receive a notification once it's completed."
                  : "Your request will be reviewed and approved within 24 hours. You'll receive a notification once it's processed."
                }
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="completion-estimate">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ background: gradient }}
              ></div>
            </div>
            <div className="estimate-text">
              Estimated completion: 
              <span style={{ color: mainColor }}>
                {isWithdrawal ? ' Within 24-48 hours' : ' Within 24 hours'}
              </span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="success-actions">
            <button 
              className="success-action-btn primary" 
              onClick={onContinue || onClose}
              style={{ background: gradient }}
            >
              View Transaction History
            </button>
            <button 
              className="success-action-btn secondary" 
              onClick={onClose}
              style={{ 
                color: mainColor, 
                borderColor: mainColor,
                background: 'transparent'
              }}
            >
              {isWithdrawal ? 'Make Another Withdrawal' : 'Top Up funds'}
            </button>
          </div>

          {/* Support info */}
          <div className="support-info">
            <p>
              Need help?{' '}
              <a href="mailto:support@trexarng.com" onClick={(e) => e.stopPropagation()}>
                Contact us
              </a>
              {' '}or call +234 900 000 0000
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessModal;